<template>
  <div :ref="timestamp" class="tooltip-wrapper" @mouseenter="openTooltip" @mouseleave="closeTooltip">
    <img v-if="iconImage" :src="iconImage" class="tooltipIcon" :style="iconStyle" alt="tooltip" />
    <VIcon v-else-if="isInfoIcon" name="info-line" connotation="cta" class="tooltipIcon" :style="iconStyle" style="font-size: 14px" />
    <p v-else-if="!isInfoIcon" class="title ion-no-margin cursorPointer"> {{ tooltipText }}</p>
    <!-- Inline tooltip used when inside a VDialog (native <dialog>/top-layer).
         The global App.vue tooltip is position:fixed z-index:99999 but cannot
         penetrate the browser top layer created by showModal(). Rendering here
         keeps the tooltip inside the dialog's DOM so it stays on top. -->
    <div v-if="floating" :class="['floating-tooltip-box', 'floating-tooltip-box--' + placement]">
      <slot></slot>
    </div>
  </div>
</template>

<script>

export default {
  name: 'Tooltip',
  inject: ['showTooltipFn'],
  props: {
    isInfoIcon: {
      type: Boolean,
      default: true
    },
    tooltipText: {
      type: String,
      default: ""
    },
    iconStyle: {
      type: Object,
      default: () => {},
    },
    iconImage: {
      type: String,
      default: null
    },
    // Set to true when inside a Vivid VDialog (or any element that uses the
    // browser top layer via showModal()). The global App.vue tooltip uses
    // position:fixed which cannot penetrate the top layer, so this flag
    // switches to an inline CSS-hover tooltip rendered inside the component's
    // own DOM — which is inside the dialog's top-layer context.
    floating: {
      type: Boolean,
      default: false
    },
    // Controls where the floating tooltip appears relative to the icon.
    // Options: 'top' | 'bottom' | 'left' | 'right'
    placement: {
      type: String,
      default: 'top'
    }
  },
  data() {
    return {
      timestamp: `tooltip_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      informationCircleOutline: 'info-line'
    }
  },
  methods: {
    openTooltip() {
      if (this.floating) return; // inline CSS hover handles it
      const icon = this.$refs[this.timestamp];
      if (!icon) return;
      this.showTooltipFn({ slot: this.$slots.default(), icon });
    },
    closeTooltip() {
      if (this.floating) return;
      this.showTooltipFn({ show: false });
    }
  }
}
</script>

<style lang="scss" scoped>
.title {
  color: inherit;
}
.tooltip-wrapper {
  display: flex;
  align-items: center;
  position: relative; // anchor for the floating tooltip box
}
.tooltipIcon{
  color: var(--purple);
  cursor: pointer;
  padding-top: 1px;
}
img.tooltipIcon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  padding-top: 1px;
}
// Inline tooltip rendered inside the component DOM — used when the tooltip
// is inside a VDialog (top layer) where the App.vue global tooltip can't reach.
.floating-tooltip-box {
  display: none;
  position: absolute;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 5px;
  width: 280px;
  text-wrap: wrap;
  z-index: 1;
  pointer-events: none;
  white-space: normal;
}
.tooltip-wrapper:hover .floating-tooltip-box {
  display: block;
}
// Placement variants
.floating-tooltip-box--top {
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
}
.floating-tooltip-box--bottom {
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
}
.floating-tooltip-box--left {
  right: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
}
.floating-tooltip-box--right {
  left: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
}
</style>